import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'

import { getGroupedFailedChecksCount } from '../utils/getGroupedFailedChecksCount'
import { GroupedFraudChecks } from '../utils/getGroupedFraudChecks'

import { Alert, CardDashboard, Color, Flex, Icon, Tooltip, Typography } from '@percent/lemonade'
import styles from './FraudFlagsPanel.module.scss'
import { FraudFlagsPanelContentProps, FraudFlagsPanelProps } from './FraudFlagsPanel.types'

const fraudFlagWarningThreshold = 4 as const

type FlagColor = Extract<Color, 'alert400' | 'warning400'>
type FraudFlagStatus = 'warning' | 'danger'

const getFraudFlagStatus = (weight?: number): FraudFlagStatus => {
  if (weight !== undefined && weight <= fraudFlagWarningThreshold) return 'warning'

  return 'danger'
}

const getFlagColor = (status: FraudFlagStatus): FlagColor => {
  switch (status) {
    case 'warning':
      return 'warning400'
    case 'danger':
      return 'alert400'
    default:
      return 'alert400'
  }
}

const getStatusTooltip = (status: FraudFlagStatus, t: TFunction): string => {
  switch (status) {
    case 'warning':
      return t('autoFraudChecks.fraudFlags.tooltip.warning')
    case 'danger':
      return t('autoFraudChecks.fraudFlags.tooltip.danger')
    default:
      return t('autoFraudChecks.fraudFlags.tooltip.danger')
  }
}

type FlagItemProps = {
  status: FraudFlagStatus
  label: string
}

function FlagItem({ status, label }: FlagItemProps) {
  const { t } = useTranslation()

  return (
    <div className={styles.flagItem}>
      <Tooltip content={getStatusTooltip(status, t)}>
        <div className={styles.flagIconWrapper}>
          <Icon name="flag" color={getFlagColor(status)} size={5} />
        </div>
      </Tooltip>
      <Typography>{label}</Typography>
    </div>
  )
}

function AgentFlagsSection({ failedChecks }: { failedChecks: GroupedFraudChecks['agent']['failedChecks'] }) {
  const { t } = useTranslation()

  if (failedChecks.length === 0) return null

  return (
    <div>
      <Typography variant="h5" className={styles.flagTitle}>
        {t('autoFraudChecks.fraudFlags.agentFlags')}
      </Typography>
      {failedChecks.map(check => (
        <FlagItem
          key={check.checkName}
          status={getFraudFlagStatus(check.weight)}
          label={t(`autoFraudChecks.agent.${check.checkName}`)}
        />
      ))}
    </div>
  )
}

function DocumentFlagsSection({ failedChecks }: { failedChecks: GroupedFraudChecks['documents']['failedChecks'] }) {
  const { t } = useTranslation()

  if (failedChecks.length === 0) return null

  return (
    <div>
      <Typography variant="h5" className={styles.flagTitle}>
        {t('autoFraudChecks.fraudFlags.documentFlags')}
      </Typography>
      {failedChecks.map(check => (
        <FlagItem
          key={check.checkName}
          status={getFraudFlagStatus(check.weight)}
          label={t(`autoFraudChecks.document.${check.checkName}`)}
        />
      ))}
    </div>
  )
}

function DomainFlagsSection({ domains }: { domains: GroupedFraudChecks['domains'] }) {
  const { t } = useTranslation()

  const domainsWithFailures = domains.filter(d => d.failedChecks.total > 0)

  if (domainsWithFailures.length === 0) return null

  return (
    <div>
      <Typography variant="h5" className={styles.flagTitle}>
        {t('typography.domainFlags')}
      </Typography>
      {domainsWithFailures.map(domain => (
        <div key={domain.name} className={styles.domainGroup}>
          <Typography variant="default-bold">{t('autoFraudChecks.domainName', { name: domain.name })}</Typography>
          {domain.failedChecks.basic?.map(check => (
            <FlagItem
              key={`${domain.name}-basic-${check.name}`}
              status={getFraudFlagStatus(check.weight)}
              label={t(`autoFraudChecks.basicDomain.${check.name}`)}
            />
          ))}
          {domain.failedChecks.advanced?.map(check => (
            <FlagItem
              key={`${domain.name}-advanced-${check.name}`}
              status={getFraudFlagStatus(check.weight)}
              label={t(`autoFraudChecks.advancedDomain.${check.name}`)}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export function FraudFlagsPanelContent({ groupedFraudChecks, fraudScore }: FraudFlagsPanelContentProps) {
  const { t } = useTranslation()

  const totalFailed = getGroupedFailedChecksCount(groupedFraudChecks)

  if (totalFailed === 0) return null

  // A score of 0 is still a score, so check the type rather than truthiness. Without one, show the flag count.
  const alertTitle =
    typeof fraudScore === 'number'
      ? t('autoFraudChecks.fraudFlags.fraudScore', { score: fraudScore })
      : t('autoFraudChecks.fraudFlags.warningAlert', { count: totalFailed })

  return (
    <Flex direction="column" gap={16}>
      <Alert variant="warning" title={alertTitle} />
      <DocumentFlagsSection failedChecks={groupedFraudChecks.documents.failedChecks} />
      <DomainFlagsSection domains={groupedFraudChecks.domains} />
      <AgentFlagsSection failedChecks={groupedFraudChecks.agent.failedChecks} />
    </Flex>
  )
}

export function FraudFlagsPanel({
  groupedFraudChecks,
  isAutoFraudCheckEnabled,
  fraudScore,
  withCard = true
}: FraudFlagsPanelProps) {
  const { t } = useTranslation()

  if (!isAutoFraudCheckEnabled || !groupedFraudChecks) return null

  if (!withCard) {
    return <FraudFlagsPanelContent groupedFraudChecks={groupedFraudChecks} fraudScore={fraudScore} />
  }

  return (
    <CardDashboard title={t('autoFraudChecks.fraudFlags.title')} className={styles.card}>
      <FraudFlagsPanelContent groupedFraudChecks={groupedFraudChecks} fraudScore={fraudScore} />
    </CardDashboard>
  )
}
